import { Search, CalendarIcon, Plus, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/card";

interface Property {
  id: string;
  title: string;
  address: string;
  price: number;
  specifications: {
    bedrooms: number;
    bathrooms: number;
    area: number;
    parking: number;
  };
  status: PropertyStatus;
  owner: {
    id: string;
    name: string;
    email: string;
    phone: string;
    whatsapp: string;
  };
}

type PropertyStatus = "available" | "rented" | "inactive";

const statusLabels: Record<PropertyStatus, { label: string; className: string }> = {
  available: { label: "Disponível", className: "bg-green-100 text-green-800" },
  rented: { label: "Alugada", className: "bg-blue-100 text-blue-800" },
  inactive: { label: "Inativa", className: "bg-gray-100 text-gray-800" },
};

export default function PropertiesPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      // First get the current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Then fetch properties with a simpler query first
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      // For each property, fetch the owner details separately
      const propertiesWithOwners = await Promise.all(
        (data || []).map(async (property) => {
          const { data: ownerData } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', property.user_id)
            .single();

          return {
            ...property,
            owner: ownerData || {
              id: property.user_id,
              name: 'Unknown',
              email: '',
              phone: '',
              whatsapp: ''
            }
          };
        })
      );

      setProperties(propertiesWithOwners);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar imóveis",
        description: "Não foi possível carregar a lista de imóveis.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (propertyId: string, newStatus: PropertyStatus) => {
    try {
      const { error } = await supabase
        .from('properties')
        .update({ status: newStatus })
        .eq('id', propertyId);

      if (error) throw error;

      setProperties(prev =>
        prev.map(property =>
          property.id === propertyId
            ? { ...property, status: newStatus }
            : property
        )
      );

      toast({
        title: "Status atualizado",
        description: "O status do imóvel foi atualizado com sucesso.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar status",
        description: "Não foi possível atualizar o status do imóvel.",
      });
    }
  };

  const handleSchedule = (property: Property) => {
    navigate("/agendamentos", {
      state: {
        selectedOwner: property.owner.id,
        selectedProperty: property.id
      }
    });
  };

  const handlePropertyClick = (property: Property) => {
    navigate(`/imoveis/${property.id}`, {
      state: { property }
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const filteredProperties = properties.filter((property) =>
    property.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    property.owner.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900">
            Imóveis
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            Gerencie seus imóveis cadastrados
          </p>
        </div>
        <Button onClick={() => navigate("/imoveis/novo")}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Imóvel
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Pesquisar por nome do imóvel ou proprietário..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {properties.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <Building2 className="w-12 h-12 text-gray-400" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">Nenhum imóvel cadastrado</h3>
              <p className="mt-1 text-gray-500">
                Comece cadastrando seu primeiro imóvel no sistema.
              </p>
            </div>
            <Button onClick={() => navigate("/imoveis/novo")}>
              <Plus className="w-4 h-4 mr-2" />
              Cadastrar Imóvel
            </Button>
          </div>
        </Card>
      ) : (
        <div className="bg-white rounded-xl shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Imóvel</TableHead>
                  <TableHead className="hidden md:table-cell">Especificações</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead className="hidden lg:table-cell">Proprietário</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProperties.map((property) => (
                  <TableRow 
                    key={property.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handlePropertyClick(property)}
                  >
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">{property.title}</div>
                        <div className="text-sm text-gray-500">
                          {property.address}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="text-sm space-y-1">
                        <div>{property.specifications.bedrooms} Quartos</div>
                        <div>{property.specifications.bathrooms} Banheiros</div>
                        <div>{property.specifications.area}m²</div>
                        <div>{property.specifications.parking} Vagas</div>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatCurrency(property.price)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="space-y-1">
                        <div className="font-medium">{property.owner.name}</div>
                        <div className="text-sm text-gray-500">
                          {property.owner.phone}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            className={`${statusLabels[property.status].className} px-2.5 py-0.5 rounded-full text-xs font-medium`}
                          >
                            {statusLabels[property.status].label}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {Object.entries(statusLabels).map(([status, { label }]) => (
                            <DropdownMenuItem
                              key={status}
                              onClick={() => handleStatusChange(property.id, status as PropertyStatus)}
                            >
                              {label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleSchedule(property)}
                        >
                          <CalendarIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}